import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Award, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { hcApi } from './api';

export const CERT_CHECK_EVENT = 'hc-certificate-check';
export const requestCertificateCheck = () => window.dispatchEvent(new Event(CERT_CHECK_EVENT));

const DOTS = ['#922A8C', '#4c124a', '#14A3A3', '#F2B705', '#922A8C', '#14A3A3', '#F2B705', '#4c124a', '#922A8C', '#14A3A3', '#F2B705', '#922A8C'];

export const downloadCertificate = async (cert, setBusy) => {
  setBusy(cert.id);
  try {
    const res = await hcApi.get(`/certificates/${cert.id}/download`, { responseType: 'blob' });
    const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = match ? match[1] : `HELPConnect-Certificate-${cert.number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error('Could not download your certificate. Please try again.');
  } finally {
    setBusy(null);
  }
};

const CertificateCelebration = ({ cert, onClose }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="hc-certificate-celebration">
      <div className="absolute inset-0 bg-[#4c124a]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-3xl p-8 text-center shadow-2xl overflow-hidden hc-pop">
        {DOTS.map((c, i) => (
          <span key={i} className="hc-confetti" style={{ background: c, left: `${6 + i * 8}%`, animationDelay: `${(i % 6) * 0.12}s` }} />
        ))}
        <div className="mx-auto w-20 h-20 rounded-full bg-[#F6EAF5] flex items-center justify-center">
          <Award className="w-10 h-10 text-[#922A8C]" />
        </div>
        <h2 className="text-3xl font-extrabold text-[#363636] mt-5">Congratulations!</h2>
        <p className="text-gray-600 mt-2">You earned your Certificate of Achievement for</p>
        <div className="mt-3 rounded-xl bg-[#F6EAF5] px-4 py-3 text-[#4c124a] font-bold" data-testid="hc-celebration-certificate-course">{cert.course_title}</div>
        <p className="text-xs text-gray-500 mt-2" data-testid="hc-celebration-certificate-number">Certificate Number {cert.number}</p>
        <button onClick={() => downloadCertificate(cert, setBusy)} disabled={busy === cert.id} data-testid="hc-celebration-download-btn"
          className="mt-6 w-full h-12 rounded-xl bg-[#922A8C] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#7a2275] transition-colors disabled:opacity-60">
          {busy === cert.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Certificate
        </button>
        <button onClick={() => { onClose(); navigate('/helpconnect/certificates'); }} className="mt-3 text-sm font-semibold text-[#922A8C] hover:underline block mx-auto" data-testid="hc-celebration-view-certificates">
          View all certificates
        </button>
        <button onClick={onClose} className="mt-2 text-sm text-gray-500 hover:text-[#922A8C]" data-testid="hc-certificate-celebration-close">Close</button>
      </div>
    </div>
  );
};

export const CertificateCelebrationHost = () => {
  const loc = useLocation();
  const [cert, setCert] = useState(null);
  const check = useCallback(() => {
    hcApi.get('/certificates/celebrate').then(({ data }) => data.certificate && setCert(data.certificate)).catch(() => {});
  }, []);
  useEffect(() => { check(); }, [check, loc.pathname]);
  useEffect(() => {
    window.addEventListener(CERT_CHECK_EVENT, check);
    return () => window.removeEventListener(CERT_CHECK_EVENT, check);
  }, [check]);
  if (!cert) return null;
  const close = () => {
    setCert(null);
    hcApi.post(`/certificates/${cert.id}/celebrated`).then(check).catch(() => {});
  };
  return <CertificateCelebration cert={cert} onClose={close} />;
};
